import React, { useState, useEffect } from 'react';
import { TextInput } from './common/TextInput';
import Button from './common/Button';
import { MdCalculate, MdRefresh, MdRefresh as MdLoading } from 'react-icons/md';
import Big from 'big.js';
import getExchangeRates, { getLatestExchangeRates } from '../../common/getExchangeRate';
import { ButtonGroup } from './common/ButtonGroup';

interface PriceBreakdown {
  productPriceRMB: number;
  productPriceEUR: number;
  grossWeight: number;
  volumeWeight: number;
  finalWeight: number;
  freightCost: number;
  marginCost: number;
  taxCost: number;
  pickupShippingCost: number;
  homeShippingCost: number;
  leboncoinFee: number;
  ebayFee: number;
  temuFee: number;
  finalPriceLeboncoin: number;
  finalPriceEbay: number;
  finalPriceTemu: number;
}

const CalculatorPage: React.FC = () => {
  const [productPrice, setProductPrice] = useState<string>('');
  const [pricePerKilo, setPricePerKilo] = useState<string>('1.75');
  const [marginPercent, setMarginPercent] = useState<string>('30');
  const [productWeight, setProductWeight] = useState<string>('');
  const [dimensionLength, setDimensionLength] = useState<string>('');
  const [dimensionWidth, setDimensionWidth] = useState<string>('');
  const [dimensionHeight, setDimensionHeight] = useState<string>('');
  const [exchangeRate, setExchangeRate] = useState<number>(0.13); // Default rate RMB to EUR
  const [isLoadingRate, setIsLoadingRate] = useState<boolean>(true);
  const [rateError, setRateError] = useState<string | null>(null);
  const [priceBreakdown, setPriceBreakdown] = useState<PriceBreakdown | null>(null);
  const [currency, setCurrency] = useState<'EUR' | 'RMB'>('EUR');

  // Fetch exchange rate from API
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        setIsLoadingRate(true);
        setRateError(null);
        // Fetch EUR to CNY (RMB) rate
        const rates = await getLatestExchangeRates('EUR');
        console.log(rates);
        // Get CNY rate and convert to RMB to EUR (1/CNY_rate)
        const cnyRate = rates.CNY;
        if (cnyRate) {
          setExchangeRate(1 / cnyRate); // Convert CNY to EUR rate to RMB to EUR
        } else {
          throw new Error('CNY rate not found');
        }
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        setRateError('Failed to load exchange rate. Using default rate.');
        setExchangeRate(0.13); // Fallback to default rate
      } finally {
        setIsLoadingRate(false);
      }
    };

    fetchExchangeRate();
  }, []);

  const calculateVolumeWeight = (length: Big, width: Big, height: Big): Big => {
    // Volume weight = (L × W × H) / 6000 (standard formula for air freight)
    return length.times(width).times(height).div(6000);
  };

  const calculatePriceBreakdown = (): PriceBreakdown | null => {
    const price = new Big(productPrice);
    const freightRate = new Big(pricePerKilo);
    const margin = new Big(marginPercent);
    const weight = new Big(productWeight);
    const length = new Big(dimensionLength);
    const width = new Big(dimensionWidth);
    const height = new Big(dimensionHeight);

    if (isNaN(price) || isNaN(freightRate) || isNaN(margin) || isNaN(weight) ||
      isNaN(length) || isNaN(width) || isNaN(height)) {
      return null;
    }

    // Convert RMB to EUR
    const priceEUR = currency === 'RMB' ? price.times(exchangeRate) : price;
    const priceRMB = currency === 'EUR' ? price.times(exchangeRate) : price;
    let cumulativeCost = priceEUR;

    // Calculate volume weight
    const volumeWeight = calculateVolumeWeight(length, width, height);

    // Final weight is the bigger of gross weight and volume weight
    const finalWeight = weight.gt(volumeWeight) ? weight : volumeWeight;

    // Calculate freight cost
    const freightCost = finalWeight.times(freightRate);
    cumulativeCost = cumulativeCost.plus(freightCost);

    
    // Calculate margin cost (percentage of freight cost)
    const marginCost = (priceEUR.plus(freightCost)).times(margin.div(100));
    cumulativeCost = cumulativeCost.plus(marginCost);

    // Calculate shipping cost
    // TODO: Improve with shipping rates
    const pickupShippingCost = (() => {{
      if (finalWeight.lte(0.5)) {
        return (new Big(3)).div(1.2);
      } else if (finalWeight.lte(1)) {
        return (new Big(4)).div(1.2);
      } else if (finalWeight.lte(2)) {
        return (new Big(5)).div(1.2);
      } else {
        return (new Big(6.5)).div(1.2);
      }
    }})();
    const homeShippingCost = (() => {{
      if (finalWeight.lte(2)) {
        return new Big(5.83);
      } else if (finalWeight.lte(3)) {
        return (new Big(6.5));
      } else {
        return (new Big(7));
      }
    }})();
    console.log('cumulativeCost', cumulativeCost.toFixed(2));
    
    // Calculate marketplace fees

    // Calculate final price
    const leboncoinRate = new Big(0.14);
    const priceWithoutShippingLeboncoin = cumulativeCost
      .times(1.2)
      // On final price
      .div(new Big(1).minus(leboncoinRate))
    const finalPriceLeboncoin = priceWithoutShippingLeboncoin
      .plus(pickupShippingCost.times(1.2))
    const leboncoinFee = priceWithoutShippingLeboncoin.times(leboncoinRate);
    console.log('leboncoinFee', leboncoinFee.toFixed(2));
    console.log('priceWithoutShippingLeboncoin', priceWithoutShippingLeboncoin.toFixed(2));

    const ebayRate = new Big(0.15);
    const finalPriceEbay = cumulativeCost
      .plus(pickupShippingCost)
      .times(1.2)
      // On final price including shipping
      .div(new Big(1).minus(ebayRate))
    const ebayFee = finalPriceEbay.times(ebayRate);

    const temuRate = new Big(0.11);
    const temuFee = cumulativeCost.times(temuRate);
    const finalPriceTemu = cumulativeCost
      // Intermediate fee
      .times((new Big(1).plus(temuRate)))
      .plus(homeShippingCost)
      .times(1.2);

    return {
      productPriceRMB: priceRMB,
      productPriceEUR: priceEUR,
      grossWeight: weight,
      volumeWeight: volumeWeight,
      finalWeight: finalWeight,
      freightCost: freightCost,
      marginCost: marginCost,
      taxCost: -1,
      pickupShippingCost: pickupShippingCost,
      homeShippingCost: homeShippingCost,
      leboncoinFee: leboncoinFee,
      ebayFee: ebayFee,
      temuFee: temuFee,
      finalPriceLeboncoin: finalPriceLeboncoin,
      finalPriceEbay: finalPriceEbay,
      finalPriceTemu: finalPriceTemu,
    };
  };

  const handleCalculate = () => {
    const breakdown = calculatePriceBreakdown();
    setPriceBreakdown(breakdown);
  };

  const handleReset = () => {
    setProductPrice('');
    setPricePerKilo('');
    setMarginPercent('');
    setProductWeight('');
    setDimensionLength('');
    setDimensionWidth('');
    setDimensionHeight('');
    setPriceBreakdown(null);
  };

  const formatCurrency = (amount: Big, currency: 'EUR' | 'RMB' = 'EUR'): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'EUR' ? 'EUR' : 'CNY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount.toFixed(2));
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-lg p-3 flex gap-4 mb-6">
        <TextInput
          label="Margin Percentage on import price (%)"
          type="number"
          value={marginPercent}
          onChange={(e) => setMarginPercent(e.target.value)}
          placeholder="Enter margin percentage"
          fullWidth
        />

        <TextInput
          label="Price International freight per Kilo (EUR)"
          type="number"
          value={pricePerKilo}
          onChange={(e) => setPricePerKilo(e.target.value)}
          placeholder="Enter freight cost per kilo"
          fullWidth
        />
        <div className="relative">
          <TextInput
            label="Exchange Rate (RMB to EUR)"
            type="number"
            value={exchangeRate.toString()}
            onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0.13)}
            placeholder="Enter exchange rate"
            fullWidth
            disabled={isLoadingRate}
            helpText={
              isLoadingRate
                ? "Loading current exchange rate..."
                : rateError
                  ? rateError
                  : `Current rate: 1 RMB = ${exchangeRate.toFixed(4)} EUR`
            }
          />
          {isLoadingRate && (
            <div className="absolute right-3 top-8">
              <MdLoading className="animate-spin text-gray-400" />
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-row gap-4">
        <div className="bg-white rounded-lg shadow-lg p-3" style={{ width: '400px' }}>
          <div className="space-y-4">
            <TextInput
              label={`Product Price (${currency})`}
              type="number"
              size="lg"
              value={productPrice}
              onChange={(e) => setProductPrice(e.target.value)}
              placeholder={`Enter product price in ${currency}`}
            />

            <ButtonGroup
              options={[
                { value: 'EUR', label: 'EUR' },
                { value: 'RMB', label: 'RMB' },
              ]}
              value={currency}
              onChange={(currency) => setCurrency(currency)}
            />

            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Weight (kg) and Dimensions (cm)
            </h3>

            <TextInput
              label="Product Weight (kg)"
              type="number"
              value={productWeight}
              onChange={(e) => setProductWeight(e.target.value)}
              placeholder="Enter product weight"
              fullWidth
            />

            <div className="grid grid-cols-3 gap-2">
              <TextInput
                label="Length (cm)"
                type="number"
                value={dimensionLength}
                onChange={(e) => setDimensionLength(e.target.value)}
                placeholder="Enter length"
                fullWidth
              />

              <TextInput
                label="Width (cm)"
                type="number"
                value={dimensionWidth}
                onChange={(e) => setDimensionWidth(e.target.value)}
                placeholder="Enter width"
                fullWidth
              />

              <TextInput
                label="Height (cm)"
                type="number"
                value={dimensionHeight}
                onChange={(e) => setDimensionHeight(e.target.value)}
                placeholder="Enter height"
                fullWidth
              />
            </div>


          </div>


          <div className="flex gap-4 mb-6">
            <Button
              preset="primary"
              onClick={handleCalculate}
              disabled={!productPrice || !pricePerKilo || !marginPercent || !productWeight ||
                !dimensionLength || !dimensionWidth || !dimensionHeight || isLoadingRate}
            >
              <MdCalculate className="mr-2" />
              Calculate Price
            </Button>

            <Button
              preset="white"
              onClick={handleReset}
            >
              <MdRefresh className="mr-2" />
              Reset
            </Button>
          </div>


        </div>
        <div className="flex-1 bg-white rounded-lg shadow-lg p-4 text-xl" style={{ maxWidth: '550px' }}>
          {priceBreakdown && (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b-2 border-yellow-400 bg-yellow-50 px-3 py-3 rounded">
                <span className="text-lg font-bold text-gray-900">Final Price Leboncoin:</span>
                <span className="text-right">
                  <span className="text-xl font-bold text-yellow-600">{formatCurrency(priceBreakdown.finalPriceLeboncoin)}</span>
                  <br />
                  <span className="text-gray-500 font-normal font-xs">
                    {`Displayed as: ${priceBreakdown.finalPriceLeboncoin - (priceBreakdown.pickupShippingCost * 1.2)}`} + shipping cost {formatCurrency(priceBreakdown.pickupShippingCost * 1.2)}
                  </span>
                </span>
              </div>

              <div className="flex justify-between items-center py-2 border-b-2 border-yellow-400 bg-yellow-50 px-3 py-3 rounded">
                <span className="text-lg font-bold text-gray-900">Final Price Ebay:</span>
                <span className="text-xl font-bold text-yellow-600">{formatCurrency(priceBreakdown.finalPriceEbay)}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b-2 border-yellow-400 bg-yellow-50 px-3 py-3 rounded">
                <span className="text-lg font-bold text-gray-900">Final Price Temu:</span>
                <span className="text-xl font-bold text-yellow-600">{formatCurrency(priceBreakdown.finalPriceTemu)}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Product Price:</span>
                <span className="font-semibold">
                  {formatCurrency(priceBreakdown.productPriceEUR)}
                  ({formatCurrency(priceBreakdown.productPriceRMB, 'RMB')})
                </span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Weight:</span>
                <span className="font-semibold text-right">
                  {priceBreakdown.finalWeight.toFixed(2)} kg
                  <br />
                  <span className="text-gray-500 font-normal font-xs">
                    (gross {priceBreakdown.grossWeight.toFixed(2)} kg, volume {priceBreakdown.volumeWeight.toFixed(2)} kg)
                  </span>
                </span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">International freight cost:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.freightCost)}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Our margin:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.marginCost)}</span>
              </div>


              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Pickup shipping cost:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.pickupShippingCost)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Home shipping cost:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.homeShippingCost)}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Leboncoin fee:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.leboncoinFee)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Ebay fee:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.ebayFee)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Temu fee:</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.temuFee)}</span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="">Tax (20% VAT):</span>
                <span className="font-semibold">{formatCurrency(priceBreakdown.taxCost)}</span>
              </div>


            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalculatorPage;
